/*
 * This file is part of glowingblue/gamilytics.
 *
 * Copyright (c) 2022 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import Tooltip from 'flarum/common/components/Tooltip';
import icon from 'flarum/common/helpers/icon';
import avatar from 'flarum/common/helpers/avatar';
import username from 'flarum/common/helpers/username';
import abbreviateNumber from 'flarum/common/utils/abbreviateNumber';
import { slug } from '../../../common';
import ItemList from 'flarum/common/utils/ItemList';

// Make translation calls shorter
const t = (...args) => app.translator.trans(...args);
const prfx = `${slug}.forum.leaderboard`;

export default class LeaderboardListItem extends Component {
	oninit(vnode) {
		super.oninit(vnode);
	}

	view() {
		const { user } = this.attrs;

		return (
			<div className="LeaderboardListItem">
				<Link href={app.route.user(user)}>{this.items().toArray()}</Link>
			</div>
		);
	}

	items() {
		const { user, position } = this.attrs;

		const items = new ItemList();

		items.add('position', <span className="LeaderboardListItem-position">{position.toString().padStart(2, '0')}</span>, 100);

		items.add('avatar', avatar(user), 70);

		items.add(
			'username',
			<div className="LeaderboardListItem-username">
				{username(user)}

				{user.lastSeenAt() && user.isOnline() && (
					<div className="UserCard-lastSeen online">
						{icon('fas fa-circle')} {t('core.forum.user.online_text')}
					</div>
				)}
			</div>,
			40
		);

		items.add(
			'count',
			<Tooltip text={this.tooltipText}>
				<span className="LeaderboardListItem-count">
					{icon(this.icon)}
					{abbreviateNumber(this.value)}
				</span>
			</Tooltip>,
			10
		);

		return items;
	}

	get type() {
		return app.leaderboard.activityType;
	}

	get value() {
		const { user } = this.attrs;
		return Number.parseInt(user.activitySummary());
	}

	get tooltipText() {
		if (this.type === 'time_spent') {
			const text = dayjs.duration(this.value, 'minutes').humanize();
			return t(`${prfx}.time_spent_tooltip`, {
				text,
				duration: this.value,
			});
		}

		return `${this.value.toLocaleString()} ${t(`${prfx}.nav.${this.type}`)}`;
	}

	get icon() {
		return this.iconMap[this.type];
	}

	get iconMap() {
		return LeaderboardListItem.getIconMap();
	}

	static getIconMap() {
		return {
			exp: 'far fa-hiking',
			discussions: 'far fa-comments',
			comments: 'far fa-comment',
			likes_received: 'far fa-thumbs-up',
			likes_given: 'far fa-thumbs-up',
			time_spent: 'far fa-hourglass-half',
			best_answers: 'far fa-badge-check',
			upvotes_received: 'far fa-thumbs-up',
			upvotes_given: 'far fa-thumbs-up',
			downvotes_received: 'far fa-thumbs-down',
			downvotes_given: 'far fa-thumbs-down',
			following_tags: 'far fa-star',
			lurking_tags: 'far fa-stars',
			ignoring_tags: 'far fa-star-exclamation',
			hiding_tags: 'far fa-ban',
			following_users: 'fas fa-user-friends',
			lurking_users: 'fas fa-user-friends',
			followed_by_users: 'fas fa-user-friends',
			lurked_by_users: 'fas fa-user-friends',
			popular_discussion: 'far fa-fire',
		};
	}
}
